import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  AlignmentType,
  HeadingLevel,
  PageNumber,
  Header,
  Footer,
} from 'docx';
import { saveAs } from 'file-saver';
import {
  createHeading,
  createParagraph,
  createTextRun,
  createInfoTable,
  createScoreTable,
  createErrorTable,
  createBoxedParagraph,
  createPageBreak,
  formatDate,
  getGradeText,
  getScoreColor,
  COLORS,
} from '../utils/docxHelpers';
import { generateComments } from '../utils/generateComments';
import { getTopicFromEssay } from '../utils/topicHelper';
import {
  Correction,
  ErrorRecord,
  Essay,
  DimensionScores,
  CorrectedVersionSection,
  EssayTopic,
  EssayTypes,
} from '../types';

// DOCX导出数据接口
interface DOCXExportData {
  correction: Correction;
  errors?: ErrorRecord[];
  essay?: Essay;
  dimension_scores?: DimensionScores;
  corrected_version?: CorrectedVersionSection;
}

// 主导出函数
export async function generateCorrectionDOCX(data: DOCXExportData): Promise<void> {
  const { correction, errors = [], essay, dimension_scores, corrected_version } = data;

  // 获取作文相关信息
  const topic = essay ? getTopicFromEssay(essay) : null;
  const essayType = topic?.essay_type || EssayTypes.CONTINUATION;
  const isApplication = essayType === EssayTypes.APPLICATION;

  // 创建文档内容
  const sections = [];

  // 第1页：封面
  const coverPage = createCoverPage({ essay, topic, correction });
  sections.push(...coverPage);
  sections.push(createPageBreak());

  // 第2页：评分详情
  const scorePage = createScorePage({ correction, essay, topic, dimension_scores, isApplication });
  sections.push(...scorePage);
  sections.push(createPageBreak());

  // 第3页：原文批注
  const annotatedPage = createAnnotatedTextPage({ essay, errors });
  sections.push(...annotatedPage);

  // 第4页：错误分析（如果有错误）
  if (errors.length > 0) {
    sections.push(createPageBreak());
    const errorPage = createErrorAnalysisPage({ errors, essay });
    sections.push(...errorPage);
  }

  // 第5页：进阶范文（如果有）
  if (corrected_version?.full_text) {
    sections.push(createPageBreak());
    const modelPage = createModelEssayPage({ corrected_version });
    sections.push(...modelPage);
  }

  // 创建文档
  const doc = new Document({
    creator: '英语作文批改系统',
    title: `批改报告_${essay?.student?.name || '未知学生'}_${formatDate(new Date())}`,
    styles: {
      default: {
        document: {
          run: {
            font: '微软雅黑',
            size: 22, // 11pt
          },
          paragraph: {
            spacing: {
              line: 360, // 1.5倍行距
              before: 120,
              after: 120,
            },
          },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1440,    // 1 inch
            right: 1440,
            bottom: 1440,
            left: 1440,
          },
        },
      },
      headers: {
        default: new Header({
          children: [
            createParagraph('英语作文批改报告', {
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),
          ],
        }),
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  children: ['第 ', PageNumber.CURRENT, ' 页，共 ', PageNumber.TOTAL_PAGES, ' 页'],
                }),
              ],
            }),
          ],
        }),
      },
      children: sections as any[], // 支持 Paragraph 和 Table 混合类型
    }],
  });

  // 生成文件并下载
  const blob = await Packer.toBlob(doc);
  const studentName = essay?.student?.name || '未知学生';
  const date = formatDate(new Date());
  const filename = `批改报告_${studentName}_${date}.docx`;
  saveAs(blob, filename);
}

// 创建封面页
function createCoverPage(data: {
  essay?: Essay;
  topic?: EssayTopic | null;
  correction: Correction;
}): (Paragraph | Table)[] {
  const { essay, topic } = data;
  const elements: (Paragraph | Table)[] = [];

  // 大标题
  elements.push(
    createHeading('英语作文批改报告', HeadingLevel.TITLE, {
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 },
    })
  );

  // 分隔线（使用下划线模拟）
  elements.push(
    createParagraph('━'.repeat(50), {
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  );

  // 学生信息表格
  if (essay?.student || essay?.class) {
    const studentInfo = [];
    if (essay.student?.name) {
      studentInfo.push({ label: '姓名', value: essay.student.name });
    }
    if (essay.class?.grade) {
      const gradeText = essay.class.grade <= 9
        ? `初${['一', '二', '三'][essay.class.grade - 7] || essay.class.grade}`
        : `高${['一', '二', '三'][essay.class.grade - 10] || essay.class.grade}`;
      studentInfo.push({ label: '年级', value: gradeText });
    }
    if (essay.class?.name) {
      studentInfo.push({ label: '班级', value: essay.class.name });
    }
    studentInfo.push({ label: '批改日期', value: formatDate(new Date()) });

    elements.push(
      createHeading('学生信息', HeadingLevel.HEADING_2, {
        spacing: { before: 400, after: 200 },
      })
    );
    elements.push(createInfoTable(studentInfo));
    elements.push(createParagraph(' ', { spacing: { after: 400 } })); // 空行
  }

  // 作文题目信息
  if (topic) {
    elements.push(
      createHeading('作文题目', HeadingLevel.HEADING_2, {
        spacing: { before: 400, after: 200 },
      })
    );

    const topicInfo = [];
    topicInfo.push({ label: '题目', value: topic.title || '未命名作文' });
    topicInfo.push({
      label: '类型',
      value: topic.essay_type === EssayTypes.APPLICATION ? '应用文写作' : '读后续写',
    });
    topicInfo.push({ label: '字数要求', value: `${topic.word_limit || 150}词` });

    elements.push(createInfoTable(topicInfo));

    if (topic.requirements) {
      elements.push(
        createParagraph(' ', { spacing: { after: 200 } })
      );
      elements.push(
        createHeading('写作要求', HeadingLevel.HEADING_3, {
          spacing: { before: 200, after: 100 },
        })
      );
      elements.push(createParagraph(topic.requirements));
    }
  }

  // 页脚信息
  elements.push(
    createParagraph(' ', { spacing: { after: 800 } }) // 占位空行
  );
  elements.push(
    createParagraph('系统自动生成 | 英语作文批改系统 v2.0', {
      alignment: AlignmentType.CENTER,
      spacing: { before: 400 },
    })
  );

  return elements;
}

// 创建评分页
function createScorePage(data: {
  correction: Correction;
  essay?: Essay;
  topic?: EssayTopic | null;
  dimension_scores?: DimensionScores;
  isApplication: boolean;
}): (Paragraph | Table)[] {
  const { correction, essay, dimension_scores, isApplication } = data;
  const elements: (Paragraph | Table)[] = [];

  // 页面标题
  elements.push(
    createHeading('评分详情', HeadingLevel.HEADING_1, {
      spacing: { after: 400 },
    })
  );

  // 总分展示
  const maxScore = isApplication ? 15 : 25;
  const gradeText = getGradeText(correction.total_score, isApplication);
  const scoreColor = getScoreColor(correction.total_score, maxScore);

  // 总分信息
  elements.push(
    createParagraph([
      createTextRun('总分：', { size: 32, bold: true }),
      createTextRun(`${correction.total_score}/${maxScore}`, {
        size: 48,
        bold: true,
        color: scoreColor,
      }),
      createTextRun(`  ${gradeText}`, {
        size: 28,
        color: scoreColor,
      }),
    ], {
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  );

  // 各维度评分
  elements.push(
    createHeading('各维度评分', HeadingLevel.HEADING_2, {
      spacing: { before: 400, after: 200 },
    })
  );

  const dimensions = [
    {
      name: '语言 Language',
      score: correction.language_score,
      maxScore: dimension_scores?.language?.max_score || (isApplication ? 5 : 7),
      percentage: 0,
    },
    {
      name: '内容 Content',
      score: correction.content_score,
      maxScore: dimension_scores?.content?.max_score || (isApplication ? 6 : 9),
      percentage: 0,
    },
    {
      name: '连贯性 Coherence',
      score: correction.coherence_score,
      maxScore: dimension_scores?.coherence?.max_score || (isApplication ? 4 : 5),
      percentage: 0,
    },
  ];

  if (!isApplication) {
    dimensions.push({
      name: '创意 Creativity',
      score: correction.creativity_score,
      maxScore: dimension_scores?.creativity?.max_score || 4,
      percentage: 0,
    });
  }

  // 计算百分比
  dimensions.forEach(dim => {
    dim.percentage = (dim.score / dim.maxScore) * 100;
  });

  elements.push(createScoreTable(dimensions));

  // 评语框
  elements.push(
    createHeading('教师评语', HeadingLevel.HEADING_2, {
      spacing: { before: 400, after: 200 },
    })
  );

  const comments = generateComments(correction, essay, dimension_scores);
  elements.push(
    createBoxedParagraph(comments, {
      backgroundColor: COLORS.lightGray,
      borderColor: COLORS.primary,
    })
  );

  return elements;
}

// 创建原文批注页
function createAnnotatedTextPage(data: {
  essay?: Essay;
  errors: ErrorRecord[];
}): (Paragraph | Table)[] {
  const { essay, errors } = data;
  const elements: (Paragraph | Table)[] = [];

  // 页面标题
  elements.push(
    createHeading('原文批注', HeadingLevel.HEADING_1, {
      spacing: { after: 400 },
    })
  );

  if (!essay?.original_text && !essay?.corrected_text) {
    elements.push(
      createParagraph('暂无原文内容', {
        alignment: AlignmentType.CENTER,
      })
    );
    return elements;
  }

  // 优先使用 corrected_text（用户编辑后的文本）
  const originalText = essay.corrected_text || essay.original_text || '';

  // 将文本分段
  const paragraphs = originalText.split('\n').filter(p => p.trim());

  // 渲染每个段落
  paragraphs.forEach((paragraph, index) => {
    // 段落标题
    elements.push(
      createHeading(`Paragraph ${index + 1}:`, HeadingLevel.HEADING_3, {
        spacing: { before: 300, after: 150 },
      })
    );

    // 处理段落文本，标记错误
    const paragraphElements: TextRun[] = [];
    let lastIndex = 0;

    // 查找该段落中的所有错误
    const paragraphErrors = errors.filter(err => paragraph.includes(err.error_text));

    if (paragraphErrors.length > 0) {
      // 按错误位置排序
      const sortedErrors = paragraphErrors.sort((a, b) => {
        const posA = paragraph.indexOf(a.error_text);
        const posB = paragraph.indexOf(b.error_text);
        return posA - posB;
      });

      sortedErrors.forEach(error => {
        const errorIndex = paragraph.indexOf(error.error_text, lastIndex);

        if (errorIndex !== -1) {
          // 添加错误前的正常文本
          if (errorIndex > lastIndex) {
            paragraphElements.push(
              createTextRun(paragraph.substring(lastIndex, errorIndex))
            );
          }

          // 添加错误文本（使用红色和下划线标记）
          paragraphElements.push(
            createTextRun(error.error_text, {
              color: COLORS.error,
              underline: {
                type: 'single',
                color: COLORS.error,
              },
              bold: true,
            })
          );

          // 添加建议（使用绿色括号内显示）
          paragraphElements.push(
            createTextRun(` [→${error.suggestion}]`, {
              color: COLORS.success,
              italics: true,
              size: 20, // 稍小字体
            })
          );

          lastIndex = errorIndex + error.error_text.length;
        }
      });
    }

    // 添加剩余的文本
    if (lastIndex < paragraph.length) {
      paragraphElements.push(
        createTextRun(paragraph.substring(lastIndex))
      );
    }

    // 如果没有错误，直接添加整个段落
    if (paragraphElements.length === 0) {
      paragraphElements.push(createTextRun(paragraph));
    }

    elements.push(
      createParagraph(paragraphElements, {
        spacing: { before: 100, after: 200 },
      })
    );
  });

  // 添加图例说明
  if (errors.length > 0) {
    elements.push(
      createParagraph(' ', { spacing: { after: 400 } })
    );
    elements.push(
      createParagraph([
        createTextRun('标注说明：', { bold: true }),
        createTextRun('红色下划线', {
          color: COLORS.error,
          underline: { type: 'single', color: COLORS.error },
        }),
        createTextRun('表示错误文本，'),
        createTextRun('[→建议]', { color: COLORS.success, italics: true }),
        createTextRun('表示修改建议'),
      ], {
        alignment: AlignmentType.CENTER,
        spacing: { before: 200 },
      })
    );
  }

  return elements;
}

// 创建错误分析页
function createErrorAnalysisPage(data: {
  errors: ErrorRecord[];
  essay?: Essay;
}): (Paragraph | Table)[] {
  const { errors, essay } = data;
  const elements: (Paragraph | Table)[] = [];

  // 页面标题
  elements.push(
    createHeading('错误分析', HeadingLevel.HEADING_1, {
      spacing: { after: 400 },
    })
  );

  // 错误统计
  const errorStats = {
    grammar: errors.filter(e => e.error_type === 'grammar').length,
    spelling: errors.filter(e => e.error_type === 'spelling').length,
    vocabulary: errors.filter(e => e.error_type === 'vocabulary').length,
    structure: errors.filter(e => e.error_type === 'structure').length,
    punctuation: errors.filter(e => e.error_type === 'punctuation').length,
  };

  elements.push(
    createHeading('错误统计', HeadingLevel.HEADING_2, {
      spacing: { after: 200 },
    })
  );

  const statItems = [];
  if (errorStats.grammar > 0) statItems.push(`语法错误：${errorStats.grammar}个`);
  if (errorStats.spelling > 0) statItems.push(`拼写错误：${errorStats.spelling}个`);
  if (errorStats.vocabulary > 0) statItems.push(`词汇错误：${errorStats.vocabulary}个`);
  if (errorStats.structure > 0) statItems.push(`结构问题：${errorStats.structure}个`);
  if (errorStats.punctuation > 0) statItems.push(`标点错误：${errorStats.punctuation}个`);

  elements.push(
    createParagraph(statItems.join('  |  '), {
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  );

  // 错误详情表格
  elements.push(
    createHeading('错误详情（前10个）', HeadingLevel.HEADING_2, {
      spacing: { after: 200 },
    })
  );

  const errorData = errors.slice(0, 10).map(err => ({
    type: err.error_type,
    text: err.error_text.substring(0, 30),
    suggestion: err.suggestion.substring(0, 30),
    severity: err.severity || 'medium',
  }));

  elements.push(createErrorTable(errorData));

  // 文本数据分析
  if (essay) {
    elements.push(
      createHeading('文本数据分析', HeadingLevel.HEADING_2, {
        spacing: { before: 400, after: 200 },
      })
    );

    const analysisItems = [
      `• 实际字数：${essay.word_count || 0}词`,
      `• 段落数量：${(essay.original_text || '').split('\n').filter(p => p.trim()).length}段`,
      `• 平均句长：${calculateAvgSentenceLength(essay.original_text || '')}词`,
    ];

    analysisItems.forEach(item => {
      elements.push(createParagraph(item, { spacing: { before: 50, after: 50 } }));
    });
  }

  return elements;
}

// 创建进阶范文页
function createModelEssayPage(data: {
  corrected_version: CorrectedVersionSection;
}): (Paragraph | Table)[] {
  const { corrected_version } = data;
  const elements: (Paragraph | Table)[] = [];

  // 页面标题
  elements.push(
    createHeading('进阶范文', HeadingLevel.HEADING_1, {
      spacing: { after: 400 },
    })
  );

  // 提示信息框
  elements.push(
    createBoxedParagraph(
      '基于您的原文优点和批改建议生成的高质量范文，达到优秀水平。',
      {
        backgroundColor: 'e6f7ff',
        borderColor: '91d5ff',
      }
    )
  );

  elements.push(
    createParagraph(' ', { spacing: { after: 300 } })
  );

  // 范文正文
  elements.push(
    createHeading('范文正文', HeadingLevel.HEADING_2, {
      spacing: { after: 200 },
    })
  );

  // 将范文分段显示
  const modelParagraphs = corrected_version.full_text.split('\n').filter(p => p.trim());

  modelParagraphs.forEach((paragraph, index) => {
    elements.push(
      createParagraph(paragraph, {
        spacing: { before: 150, after: 150 },
        indent: { firstLine: 720 }, // 首行缩进
      })
    );
  });

  // 主要改进点
  if (corrected_version.major_changes && corrected_version.major_changes.length > 0) {
    elements.push(
      createParagraph(' ', { spacing: { after: 400 } })
    );
    elements.push(
      createHeading('主要改进点', HeadingLevel.HEADING_2, {
        spacing: { after: 200 },
      })
    );

    corrected_version.major_changes.slice(0, 5).forEach((change, index) => {
      elements.push(
        createParagraph([
          createTextRun(`改进 ${index + 1}: `, { bold: true, color: COLORS.primary }),
          createTextRun(change.reason),
        ], {
          spacing: { before: 150, after: 100 },
        })
      );

      elements.push(
        createParagraph([
          createTextRun('原文：', { bold: true }),
          createTextRun(change.original, { color: COLORS.darkGray }),
        ], {
          spacing: { before: 50, after: 50 },
          indent: { left: 720 },
        })
      );

      elements.push(
        createParagraph([
          createTextRun('改进：', { bold: true }),
          createTextRun(change.revised, { color: COLORS.success }),
        ], {
          spacing: { before: 50, after: 150 },
          indent: { left: 720 },
        })
      );
    });
  }

  return elements;
}

// 辅助函数：计算平均句长
function calculateAvgSentenceLength(text: string): string {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim());
  if (sentences.length === 0) return '0';

  const totalWords = sentences.reduce((sum, sentence) => {
    return sum + sentence.trim().split(/\s+/).length;
  }, 0);

  return (totalWords / sentences.length).toFixed(1);
}